'use client';

import { useTranslations } from 'next-intl';
import { destroyCookie } from 'nookies';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { DialogPortal } from '@/components/layout/modals/DialogPortal';
import type { ModalContentType } from '@/components/layout/modals/types';

interface DialogContext {
  showDialog: (body: ModalContentType) => void;
  closeDialog: () => void;
}

export const DialogContext = createContext<DialogContext>({
  showDialog: () => {},
  closeDialog: () => {},
});

export default function useDialog() {
  return useContext(DialogContext);
}

export const DialogProvider = ({
  children,
}: {
  children: (showDialog: (args: ModalContentType) => void) => React.ReactNode;
}) => {
  const [errorMessage, setErrorMessage] = useState<ModalContentType>({
    title: undefined,
    body: undefined,
    children: undefined,
    handleBackButton: undefined,
  });

  const isMountedRef = useRef(false);
  const [isShowDialog, setShowDialog] = useState(false);
  const t = useTranslations();

  const showDialog = (message: ModalContentType) => {
    if (isMountedRef.current) {
      setErrorMessage(message);
      setShowDialog(true);
    }
  };

  const closeDialog = () => {
    if (errorMessage.title === t('error-UNAUTHORIZED-title')) {
      destroyCookie(null, 'HAS_LOGGED_IN', {
        path: '/',
      });
      const isDevFamily =
        process.env.NEXT_PUBLIC_ENVIRONMENT === 'local' ||
        process.env.NEXT_PUBLIC_ENVIRONMENT === 'dev';
      const COOKIE_OPTIONS = isDevFamily
        ? { path: '/', domain: '.dev.klew.jp' } // DEV/LOCAL はドメイン付き運用
        : { path: '/' }; // 本番/STG は Host-Only
      destroyCookie(null, 'chains_is_email_verified', COOKIE_OPTIONS);
      destroyCookie(null, 'chains_is_registered', COOKIE_OPTIONS);
      window.location.assign(process.env.NEXT_PUBLIC_BASE_URL);
    }
    setShowDialog(false);
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <DialogContext.Provider value={{ showDialog, closeDialog }}>
      <DialogPortal isOpen={isShowDialog} props={errorMessage} />
      {children(showDialog)}
    </DialogContext.Provider>
  );
};
